/**
 * Tenant Design Studio — logo, palette, typography, portal themes,
 * templates, signatures, academic calendar, curriculum, and school contacts.
 * Configuration-driven: no source-code changes required for onboarding.
 */
import React, { useEffect, useMemo, useState } from "react";
import client from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Section, Loading, KPITile } from "../components/Common";
import { toast } from "sonner";
import {
  Palette, Type, Building2, CalendarDays, Signature, Mail,
  FileText, ImageUp, Sparkles, ShieldCheck, MapPin, Home,
  BookOpenCheck, GraduationCap, Save, RotateCcw, Loader2, Award,
} from "lucide-react";

const TABS = [
  { id: "identity", label: "Identity", icon: Building2 },
  { id: "extended", label: "Vision & Values", icon: Award },
  { id: "regional", label: "Regional", icon: MapPin },
  { id: "brand", label: "Brand & Logo", icon: Palette },
  { id: "digital", label: "Favicon & App Icons", icon: ImageUp },
  { id: "typography", label: "Typography", icon: Type },
  { id: "portals", label: "Portal Themes", icon: Sparkles },
  { id: "templates", label: "Templates", icon: FileText },
  { id: "signatures", label: "Signatures & Stamps", icon: Signature },
  { id: "calendar", label: "Academic Calendar", icon: CalendarDays },
  { id: "curriculum", label: "Curriculum", icon: BookOpenCheck },
  { id: "contacts", label: "Contacts", icon: Mail },
  { id: "compliance", label: "Compliance", icon: ShieldCheck },
  { id: "history", label: "History & Rollback", icon: RotateCcw },
];

const money_symbols = ["$", "£", "€", "R", "₹", "¥", "₦", "Z$"];
const timezones = ["Africa/Harare", "Africa/Johannesburg", "Africa/Lagos", "Africa/Nairobi", "Europe/London", "Asia/Dubai", "UTC"];

export default function DesignStudio() {
  const { tenant, user } = useAuth();
  const [tab, setTab] = useState("identity");
  const [design, setDesign] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  const load = () => client.get("/design-studio/config").then(({ data }) => setDesign(data));
  useEffect(() => { load(); }, []);

  const patch = async (segment, payload) => {
    setSaving(true);
    try {
      const { data } = await client.patch("/design-studio/config", { [segment]: payload });
      setDesign(data);
      toast.success("Saved");
    } catch { toast.error("Unable to save"); }
    finally { setSaving(false); }
  };

  const upload = async (asset, file) => {
    setUploading(asset);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await client.post(`/design-studio/upload/${asset}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // reflect immediately in state without full re-fetch
      setDesign((d) => {
        const patched = { ...d };
        if (asset === "logo") patched.brand = { ...d.brand, logo_data_uri: data.data_uri };
        if (asset === "crest") patched.brand = { ...d.brand, crest_data_uri: data.data_uri };
        if (asset === "principal_signature") patched.signatures = { ...d.signatures, principal_signature_data_uri: data.data_uri };
        if (asset === "stamp") patched.signatures = { ...d.signatures, school_stamp_data_uri: data.data_uri };
        return patched;
      });
      toast.success(`${asset.replace("_", " ")} uploaded`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(""); }
  };

  const reset = async () => {
    if (!confirm("Reset all design settings to defaults?")) return;
    const { data } = await client.post("/design-studio/reset-defaults");
    setDesign(data);
    toast.success("Reset to defaults");
  };

  if (!design) return <Loading label="Loading Design Studio…" />;
  const b = design.brand || {}; const ident = design.identity || {}; const sig = design.signatures || {};

  return (
    <div data-testid="design-studio-page">
      <PageHeader
        kicker={`${tenant?.name} · Tenant Design Studio`}
        title="Brand, templates and configuration"
        subtitle="Onboard schools without touching source code. Every setting here flows into every module, portal, email and PDF."
        actions={
          <button onClick={reset} data-testid="btn-reset-design" className="hl-btn-outline inline-flex items-center gap-1.5 text-sm px-3 py-2">
            <RotateCcw className="w-4 h-4" /> Reset to defaults
          </button>
        }
      />

      <div className="flex flex-wrap gap-2 mb-6" data-testid="design-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-3.5 py-2 text-xs sm:text-sm rounded-md border transition ${tab === t.id ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)] shadow-sm" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {saving && <div className="text-xs text-slate-500 mb-2 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</div>}

      {tab === "identity" && (
        <IdentityForm identity={ident} onSave={(p) => patch("identity", p)} />
      )}
      {tab === "extended" && (
        <ExtendedIdentityForm extended={design.extended_identity} onSave={(p) => patch("extended_identity", p)} />
      )}
      {tab === "regional" && (
        <RegionalForm regional={design.regional} onSave={(p) => patch("regional", p)} />
      )}
      {tab === "brand" && (
        <BrandForm brand={b} onSave={(p) => patch("brand", p)} onUpload={upload} uploading={uploading} />
      )}
      {tab === "digital" && (
        <DigitalIdentityForm digital={design.digital_identity} onSave={(p) => patch("digital_identity", p)} onUpload={upload} uploading={uploading} />
      )}
      {tab === "typography" && (
        <TypographyForm typography={design.typography} onSave={(p) => patch("typography", p)} />
      )}
      {tab === "portals" && (
        <PortalsForm themes={design.portal_themes} onSave={(p) => patch("portal_themes", p)} />
      )}
      {tab === "templates" && (
        <TemplatesForm templates={design.templates} onSave={(p) => patch("templates", p)} />
      )}
      {tab === "signatures" && (
        <SignaturesForm sig={sig} onSave={(p) => patch("signatures", p)} onUpload={upload} uploading={uploading} />
      )}
      {tab === "calendar" && (
        <CalendarForm terms={design.academic_calendar} onSave={(p) => patch("academic_calendar", p)} />
      )}
      {tab === "curriculum" && (
        <CurriculumForm curriculum={design.curriculum} onSave={(p) => patch("curriculum", p)} />
      )}
      {tab === "contacts" && (
        <ContactsForm contacts={design.contacts} onSave={(p) => patch("contacts", p)} />
      )}
      {tab === "compliance" && (
        <ComplianceForm />
      )}
      {tab === "history" && (
        <HistoryForm onRollback={async (id) => {
          if (!confirm("Roll back to this version?")) return;
          const { data } = await client.post(`/design-studio/rollback/${id}`);
          setDesign(data);
          toast.success("Rolled back");
        }} />
      )}
    </div>
  );
}

function Row({ label, children, hint }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </label>
  );
}
function TextInput({ testId, ...p }) {
  return <input data-testid={testId} className="w-full px-3 py-2 border border-slate-200 rounded text-sm" {...p} />;
}
function TextArea({ testId, rows = 4, ...p }) {
  return <textarea data-testid={testId} rows={rows} className="w-full px-3 py-2 border border-slate-200 rounded text-sm font-mono" {...p} />;
}
function Select({ children, testId, ...p }) {
  return <select data-testid={testId} className="w-full px-3 py-2 border border-slate-200 rounded text-sm" {...p}>{children}</select>;
}
function SaveButton({ onClick, testId = "btn-save" }) {
  return (
    <button onClick={onClick} data-testid={testId} className="hl-btn-accent inline-flex items-center gap-2 text-sm px-4 py-2 mt-4">
      <Save className="w-4 h-4" /> Save changes
    </button>
  );
}

function IdentityForm({ identity, onSave }) {
  const [f, setF] = useState(identity);
  useEffect(() => setF(identity), [identity]);
  return (
    <Section title="School identity" subtitle="Primary information used across every module and generated document">
      <div className="hl-card p-5 grid md:grid-cols-2 gap-4">
        <Row label="School name"><TextInput testId="input-school-name" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} /></Row>
        <Row label="Short name"><TextInput testId="input-short-name" value={f.short_name || ""} onChange={(e) => setF({ ...f, short_name: e.target.value })} /></Row>
        <Row label="Motto"><TextInput value={f.motto || ""} onChange={(e) => setF({ ...f, motto: e.target.value })} /></Row>
        <Row label="Country"><TextInput value={f.country || ""} onChange={(e) => setF({ ...f, country: e.target.value })} /></Row>
        <Row label="City"><TextInput value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })} /></Row>
        <Row label="Established"><TextInput type="number" value={f.established_year || ""} onChange={(e) => setF({ ...f, established_year: Number(e.target.value) })} /></Row>
        <Row label="Timezone">
          <Select value={f.timezone || "Africa/Harare"} onChange={(e) => setF({ ...f, timezone: e.target.value })}>
            {timezones.map((t) => <option key={t}>{t}</option>)}
          </Select>
        </Row>
        <Row label="Currency">
          <div className="grid grid-cols-2 gap-2">
            <TextInput value={f.currency || "USD"} onChange={(e) => setF({ ...f, currency: e.target.value })} />
            <Select value={f.currency_symbol || "$"} onChange={(e) => setF({ ...f, currency_symbol: e.target.value })}>
              {money_symbols.map((s) => <option key={s}>{s}</option>)}
            </Select>
          </div>
        </Row>
        <Row label="Address"><TextInput value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} /></Row>
        <Row label="Phone"><TextInput value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Row>
        <Row label="Email"><TextInput value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} /></Row>
        <Row label="Website"><TextInput value={f.website || ""} onChange={(e) => setF({ ...f, website: e.target.value })} /></Row>
      </div>
      <SaveButton onClick={() => onSave(f)} testId="btn-save-identity" />
    </Section>
  );
}

function BrandForm({ brand, onSave, onUpload, uploading }) {
  const [f, setF] = useState(brand); useEffect(() => setF(brand), [brand]);
  return (
    <Section title="Brand palette & logo" subtitle="Primary + secondary + accent colours used across dashboards, PDFs, emails and portals">
      <div className="hl-card p-5 space-y-5">
        <div className="grid md:grid-cols-3 gap-4">
          <ColorField label="Primary" value={f.primary_color} onChange={(v) => setF({ ...f, primary_color: v })} />
          <ColorField label="Secondary" value={f.secondary_color} onChange={(v) => setF({ ...f, secondary_color: v })} />
          <ColorField label="Accent" value={f.accent_color} onChange={(v) => setF({ ...f, accent_color: v })} />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <AssetUpload label="Logo" current={f.logo_data_uri || f.logo_url} onFile={(fl) => onUpload("logo", fl)} uploading={uploading === "logo"} testId="upload-logo" />
          <AssetUpload label="Crest" current={f.crest_data_uri || f.crest_url} onFile={(fl) => onUpload("crest", fl)} uploading={uploading === "crest"} testId="upload-crest" />
        </div>
        <PreviewCard f={f} />
      </div>
      <SaveButton onClick={() => onSave({ primary_color: f.primary_color, secondary_color: f.secondary_color, accent_color: f.accent_color })} testId="btn-save-brand" />
    </Section>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#002147"} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded border border-slate-200" />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="flex-1 px-3 py-2 border border-slate-200 rounded text-sm mono" />
      </div>
    </div>
  );
}

function AssetUpload({ label, current, onFile, uploading, testId }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 rounded-md bg-slate-100 border border-slate-200 grid place-items-center overflow-hidden">
          {current ? <img src={current} alt={label} className="max-w-full max-h-full" /> : <ImageUp className="w-5 h-5 text-slate-400" />}
        </div>
        <label className="hl-btn-outline text-xs px-3 py-2 cursor-pointer inline-flex items-center gap-1.5">
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageUp className="w-3 h-3" />}
          {uploading ? "Uploading…" : "Choose file"}
          <input data-testid={testId} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        </label>
      </div>
    </div>
  );
}

function PreviewCard({ f }) {
  return (
    <div className="rounded-xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${f.primary_color} 0%, ${f.secondary_color || "#04244d"} 100%)` }}>
      <div className="text-[10px] uppercase tracking-[0.32em] font-bold mb-1" style={{ color: f.accent_color }}>Executive Preview</div>
      <div className="font-display font-black text-2xl">Live brand preview</div>
      <div className="text-white/60 text-xs mt-1">Every dashboard, PDF and email will use these colours</div>
    </div>
  );
}

function TypographyForm({ typography, onSave }) {
  const [f, setF] = useState(typography); useEffect(() => setF(typography), [typography]);
  return (
    <Section title="Typography" subtitle="Applied across executive, parent, student and teacher portals">
      <div className="hl-card p-5 grid md:grid-cols-2 gap-4">
        <Row label="Display font family"><TextInput value={f.display_family || ""} onChange={(e) => setF({ ...f, display_family: e.target.value })} /></Row>
        <Row label="Body font family"><TextInput value={f.body_family || ""} onChange={(e) => setF({ ...f, body_family: e.target.value })} /></Row>
        <Row label="Density">
          <Select value={f.scale || "comfortable"} onChange={(e) => setF({ ...f, scale: e.target.value })}>
            <option>comfortable</option><option>compact</option><option>spacious</option>
          </Select>
        </Row>
      </div>
      <SaveButton onClick={() => onSave(f)} testId="btn-save-typography" />
    </Section>
  );
}

function PortalsForm({ themes, onSave }) {
  const [f, setF] = useState(themes); useEffect(() => setF(themes), [themes]);
  const portals = Object.keys(f || {});
  return (
    <Section title="Portal accents" subtitle="Fine-tune the accent colour that appears inside each role portal">
      <div className="hl-card p-5 grid md:grid-cols-2 gap-4">
        {portals.map((p) => (
          <div key={p} className="p-3 rounded border border-slate-200">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 capitalize">{p} portal</div>
            <ColorField label="Accent" value={f[p]?.accent} onChange={(v) => setF({ ...f, [p]: { ...f[p], accent: v } })} />
          </div>
        ))}
      </div>
      <SaveButton onClick={() => onSave(f)} testId="btn-save-portals" />
    </Section>
  );
}

function TemplatesForm({ templates, onSave }) {
  const [f, setF] = useState(templates); useEffect(() => setF(templates), [templates]);
  const keys = Object.keys(f || {});
  return (
    <Section title="Report cards, certificates, transcripts & emails" subtitle="Handlebars-style placeholders like {{school_name}} are supported">
      <div className="hl-card p-5 space-y-4">
        {keys.map((k) => (
          <Row key={k} label={k.replace(/_/g, " ")} hint="Placeholders: {{school_name}} {{academic_year}} {{term}} {{student_name}} {{principal_name}} {{principal_title}}">
            <TextArea rows={k.includes("body") || k.includes("intro") ? 4 : 2} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          </Row>
        ))}
      </div>
      <SaveButton onClick={() => onSave(f)} testId="btn-save-templates" />
    </Section>
  );
}

function SignaturesForm({ sig, onSave, onUpload, uploading }) {
  const [f, setF] = useState(sig); useEffect(() => setF(sig), [sig]);
  return (
    <Section title="Signatures & school stamp" subtitle="Embedded on every PDF report card, certificate and transcript">
      <div className="hl-card p-5 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <Row label="Principal name"><TextInput value={f.principal_name || ""} onChange={(e) => setF({ ...f, principal_name: e.target.value })} /></Row>
          <Row label="Principal title"><TextInput value={f.principal_title || ""} onChange={(e) => setF({ ...f, principal_title: e.target.value })} /></Row>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <AssetUpload label="Principal signature (image)" current={f.principal_signature_data_uri} onFile={(fl) => onUpload("principal_signature", fl)} uploading={uploading === "principal_signature"} testId="upload-signature" />
          <AssetUpload label="Official school stamp" current={f.school_stamp_data_uri} onFile={(fl) => onUpload("stamp", fl)} uploading={uploading === "stamp"} testId="upload-stamp" />
        </div>
      </div>
      <SaveButton onClick={() => onSave({ principal_name: f.principal_name, principal_title: f.principal_title })} testId="btn-save-signatures" />
    </Section>
  );
}

function CalendarForm({ terms, onSave }) {
  const [rows, setRows] = useState(terms || []); useEffect(() => setRows(terms || []), [terms]);
  const update = (i, key, val) => setRows(rows.map((r, ix) => ix === i ? { ...r, [key]: val } : r));
  return (
    <Section title="Academic calendar" subtitle="Terms, exam windows and holiday periods that drive scheduling everywhere">
      <div className="hl-card overflow-hidden">
        <table className="hl-table w-full text-sm">
          <thead><tr><th>Term</th><th>Start</th><th>End</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td><TextInput value={r.term} onChange={(e) => update(i, "term", e.target.value)} /></td>
                <td><TextInput type="date" value={r.start} onChange={(e) => update(i, "start", e.target.value)} /></td>
                <td><TextInput type="date" value={r.end} onChange={(e) => update(i, "end", e.target.value)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => setRows([...rows, { term: `Term ${rows.length + 1}`, start: "", end: "" }])} className="hl-btn-outline text-xs px-3 py-1.5">Add term</button>
      </div>
      <SaveButton onClick={() => onSave(rows)} testId="btn-save-calendar" />
    </Section>
  );
}

function CurriculumForm({ curriculum, onSave }) {
  const [f, setF] = useState(curriculum); useEffect(() => setF(curriculum), [curriculum]);
  const toggle = (b) => {
    const boards = f.boards || [];
    setF({ ...f, boards: boards.includes(b) ? boards.filter((x) => x !== b) : [...boards, b] });
  };
  return (
    <Section title="Curriculum" subtitle="Examination boards + default grading system">
      <div className="hl-card p-5 space-y-4">
        <Row label="Examination boards">
          <div className="flex flex-wrap gap-2">
            {["Cambridge", "ZIMSEC", "IB", "CAPS", "IGCSE", "Edexcel", "SATs", "American High School"].map((b) => (
              <button key={b} onClick={() => toggle(b)} data-testid={`board-${b}`}
                className={`text-xs px-3 py-1.5 rounded border ${(f.boards || []).includes(b) ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)]" : "bg-white border-slate-200"}`}>
                {b}
              </button>
            ))}
          </div>
        </Row>
        <Row label="Default grading scale">
          <Select value={f.default_grading || "cambridge_igcse"} onChange={(e) => setF({ ...f, default_grading: e.target.value })}>
            {Object.keys(f.grading_scales || {}).map((k) => <option key={k}>{k}</option>)}
          </Select>
        </Row>
      </div>
      <SaveButton onClick={() => onSave(f)} testId="btn-save-curriculum" />
    </Section>
  );
}

function ContactsForm({ contacts, onSave }) {
  const [f, setF] = useState(contacts); useEffect(() => setF(contacts), [contacts]);
  const keys = ["principal", "admissions", "finance", "health", "boarding", "transport", "hr"];
  return (
    <Section title="Departmental contacts" subtitle="Automatically inserted into portal footers, emails and receipts">
      <div className="hl-card p-5 grid md:grid-cols-2 gap-4">
        {keys.map((k) => (
          <Row key={k} label={`${k} email`}>
            <TextInput value={f?.[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          </Row>
        ))}
      </div>
      <SaveButton onClick={() => onSave(f)} testId="btn-save-contacts" />
    </Section>
  );
}

// ---------------------------------------------------------------------------
// §3-D Extended Identity — vision, mission, core values, history, GPS
// ---------------------------------------------------------------------------
function ExtendedIdentityForm({ extended, onSave }) {
  const [f, setF] = useState(extended || {}); useEffect(() => setF(extended || {}), [extended]);
  const values = Array.isArray(f.core_values) ? f.core_values : [];
  return (
    <Section title="Vision, mission & institutional profile" subtitle="Optional — displayed on report covers, principal letters and executive publications">
      <div className="hl-card p-5 space-y-4">
        <Row label="Vision"><TextArea testId="input-vision" rows={3} value={f.vision || ""} onChange={(e) => setF({ ...f, vision: e.target.value })} /></Row>
        <Row label="Mission"><TextArea testId="input-mission" rows={3} value={f.mission || ""} onChange={(e) => setF({ ...f, mission: e.target.value })} /></Row>
        <Row label="Core values (comma-separated)" hint="Rendered as pills on the report cover and cited in AI narratives">
          <TextInput testId="input-core-values" value={values.join(", ")} onChange={(e) => setF({ ...f, core_values: e.target.value.split(",").map((v) => v.trim()).filter(Boolean) })} />
        </Row>
        <Row label="Institution history"><TextArea testId="input-history" rows={4} value={f.history || ""} onChange={(e) => setF({ ...f, history: e.target.value })} /></Row>
        <Row label="Institution profile"><TextArea testId="input-profile" rows={4} value={f.profile || ""} onChange={(e) => setF({ ...f, profile: e.target.value })} /></Row>
        <div className="grid md:grid-cols-2 gap-4">
          <Row label="GPS latitude" hint="Optional — used for district reports and maps"><TextInput testId="input-gps-lat" type="number" step="0.000001" value={f.gps_latitude ?? ""} onChange={(e) => setF({ ...f, gps_latitude: e.target.value === "" ? null : Number(e.target.value) })} /></Row>
          <Row label="GPS longitude"><TextInput testId="input-gps-lng" type="number" step="0.000001" value={f.gps_longitude ?? ""} onChange={(e) => setF({ ...f, gps_longitude: e.target.value === "" ? null : Number(e.target.value) })} /></Row>
        </div>
      </div>
      <SaveButton onClick={() => onSave(f)} testId="btn-save-extended" />
    </Section>
  );
}

// ---------------------------------------------------------------------------
// §3-E Regional Configuration
// ---------------------------------------------------------------------------
function RegionalForm({ regional, onSave }) {
  const [f, setF] = useState(regional || {}); useEffect(() => setF(regional || {}), [regional]);
  return (
    <Section title="Regional configuration" subtitle="Automatically propagates to reports, invoices, timetables and PDF exports">
      <div className="hl-card p-5 grid md:grid-cols-2 gap-4">
        <Row label="Province / State"><TextInput testId="input-province" value={f.province || ""} onChange={(e) => setF({ ...f, province: e.target.value })} /></Row>
        <Row label="District"><TextInput testId="input-district" value={f.district || ""} onChange={(e) => setF({ ...f, district: e.target.value })} /></Row>
        <Row label="Language">
          <Select value={f.language || "en-GB"} onChange={(e) => setF({ ...f, language: e.target.value })}>
            {["en-GB", "en-US", "en-ZW", "en-ZA", "fr-FR", "es-ES", "pt-PT", "sn-ZW", "nd-ZW", "sw-KE"].map((l) => <option key={l}>{l}</option>)}
          </Select>
        </Row>
        <Row label="Paper size">
          <Select value={f.paper_size || "A4"} onChange={(e) => setF({ ...f, paper_size: e.target.value })}>
            {["A4", "A3", "A5", "Letter", "Legal"].map((p) => <option key={p}>{p}</option>)}
          </Select>
        </Row>
        <Row label="Date format" hint="Applied to all generated reports">
          <Select value={f.date_format || "DD MMM YYYY"} onChange={(e) => setF({ ...f, date_format: e.target.value })}>
            {["DD MMM YYYY", "DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "D MMMM YYYY"].map((p) => <option key={p}>{p}</option>)}
          </Select>
        </Row>
        <Row label="Number format">
          <Select value={f.number_format || "en-GB"} onChange={(e) => setF({ ...f, number_format: e.target.value })}>
            {["en-GB", "en-US", "de-DE", "fr-FR", "es-ES"].map((p) => <option key={p}>{p}</option>)}
          </Select>
        </Row>
      </div>
      <SaveButton onClick={() => onSave(f)} testId="btn-save-regional" />
    </Section>
  );
}

// ---------------------------------------------------------------------------
// §3-G Digital identity — favicon & app icons
// ---------------------------------------------------------------------------
function DigitalIdentityForm({ digital, onSave, onUpload, uploading }) {
  const [f, setF] = useState(digital || {}); useEffect(() => setF(digital || {}), [digital]);
  const og = f.og_metadata || {};
  return (
    <Section title="Favicon & digital identity" subtitle="Tenant-specific browser & mobile identity — no HTML editing required">
      <div className="hl-card p-5 space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <AssetUpload label="Browser favicon (32×32)" current={f.favicon_data_uri || f.favicon_url} onFile={(fl) => onUpload("favicon", fl)} uploading={uploading === "favicon"} testId="upload-favicon" />
          <AssetUpload label="Mobile icon" current={f.mobile_icon_data_uri} onFile={(fl) => onUpload("mobile_icon", fl)} uploading={uploading === "mobile_icon"} testId="upload-mobile-icon" />
          <AssetUpload label="Application icon" current={f.app_icon_data_uri} onFile={(fl) => onUpload("app_icon", fl)} uploading={uploading === "app_icon"} testId="upload-app-icon" />
          <AssetUpload label="Shortcut icon" current={f.shortcut_icon_data_uri} onFile={(fl) => onUpload("shortcut_icon", fl)} uploading={uploading === "shortcut_icon"} testId="upload-shortcut-icon" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Row label="Browser tab title suffix" hint="Appended to every browser tab (e.g., ' · Education360')"><TextInput value={f.browser_title_suffix || ""} onChange={(e) => setF({ ...f, browser_title_suffix: e.target.value })} /></Row>
          <Row label="Loading animation label"><TextInput value={f.loading_animation_label || ""} onChange={(e) => setF({ ...f, loading_animation_label: e.target.value })} /></Row>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Row label="OG title"><TextInput value={og.og_title || ""} onChange={(e) => setF({ ...f, og_metadata: { ...og, og_title: e.target.value } })} /></Row>
          <Row label="OG description"><TextInput value={og.og_description || ""} onChange={(e) => setF({ ...f, og_metadata: { ...og, og_description: e.target.value } })} /></Row>
          <Row label="OG image URL"><TextInput value={og.og_image_url || ""} onChange={(e) => setF({ ...f, og_metadata: { ...og, og_image_url: e.target.value } })} /></Row>
        </div>
      </div>
      <SaveButton onClick={() => onSave({ browser_title_suffix: f.browser_title_suffix, loading_animation_label: f.loading_animation_label, og_metadata: f.og_metadata })} testId="btn-save-digital" />
    </Section>
  );
}

// ---------------------------------------------------------------------------
// §3-B Brand Compliance validator
// ---------------------------------------------------------------------------
function ComplianceForm() {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const run = async () => {
    setRunning(true);
    try {
      const { data } = await client.post("/design-studio/validate");
      setReport(data);
      if (!data.publishable) toast.error(`${data.counts.fail} FAIL(s) block publication`);
      else if (data.counts.warning > 0) toast(`${data.counts.warning} warning(s)`, { icon: "⚠️" });
      else toast.success("Brand PASSES all checks");
    } catch { toast.error("Validation failed"); }
    finally { setRunning(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);
  const badge = (s) => {
    const cls = s === "PASS" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : s === "WARNING" ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-rose-50 text-rose-700 border-rose-200";
    return <span className={`text-[10px] uppercase tracking-widest border rounded px-2 py-0.5 ${cls}`}>{s}</span>;
  };
  return (
    <Section title="Brand compliance validation" subtitle="Failures block publication; warnings may be bypassed by an authorised administrator">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={run} disabled={running} data-testid="btn-run-compliance" className="hl-btn-accent inline-flex items-center gap-2 text-sm px-4 py-2 disabled:opacity-60">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {running ? "Validating…" : "Run compliance scan"}
        </button>
        {report && (
          <div className="text-xs text-slate-500">
            Overall: <span className="font-bold">{report.overall}</span> ·
            {" "}Publishable: <span className="font-bold">{report.publishable ? "YES" : "NO"}</span> ·
            {" "}<span className="text-emerald-700">{report.counts.pass} PASS</span> ·
            {" "}<span className="text-amber-700">{report.counts.warning} WARN</span> ·
            {" "}<span className="text-rose-700">{report.counts.fail} FAIL</span>
          </div>
        )}
      </div>
      {report && (
        <div className="hl-card overflow-hidden" data-testid="compliance-report">
          <table className="hl-table w-full text-sm">
            <thead><tr><th>Check</th><th>Status</th><th>Message</th></tr></thead>
            <tbody>
              {report.checks.map((c) => (
                <tr key={c.check} data-testid={`compliance-${c.check.replace(/\./g, "-")}`}>
                  <td className="font-mono text-xs">{c.check}</td>
                  <td>{badge(c.status)}</td>
                  <td className="text-slate-600">{c.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// §3-A Version History + rollback
// ---------------------------------------------------------------------------
function HistoryForm({ onRollback }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/design-studio/history?limit=30");
      setEntries(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  return (
    <Section title="Change history & rollback" subtitle="Every design change is audited. Rolling back records a new version so lineage is preserved.">
      {loading ? <Loading label="Loading history…" /> : (
        <div className="hl-card overflow-hidden" data-testid="history-list">
          <table className="hl-table w-full text-sm">
            <thead><tr><th>Version</th><th>When</th><th>Author</th><th>Role</th><th>Description</th><th>Changes</th><th></th></tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} data-testid={`history-${e.version}`}>
                  <td className="font-mono text-xs">v{e.version}</td>
                  <td className="text-xs text-slate-500">{(e.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="text-xs">{e.author_email || "—"}</td>
                  <td className="text-xs uppercase tracking-wider text-slate-500">{e.author_role || ""}</td>
                  <td className="text-xs">{e.description}</td>
                  <td className="text-xs text-slate-500">{(e.changes || []).length} field(s)</td>
                  <td>
                    <button onClick={() => onRollback(e.id).then(load)} data-testid={`btn-rollback-${e.version}`} className="hl-btn-outline text-xs px-2 py-1 inline-flex items-center gap-1">
                      <RotateCcw className="w-3 h-3" /> Rollback
                    </button>
                  </td>
                </tr>
              ))}
              {!entries.length && (<tr><td colSpan={7} className="text-center text-slate-400 py-6 text-sm italic">No changes recorded yet.</td></tr>)}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
